import * as React from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {useEffect, useRef, useState} from 'react';

import {EpiImage} from "features/search/types";
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {GalleryType} from "features/template/types";
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {useModal} from "features/modal/hooks/useModal";
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {gtmEventsGalleryOpen, gtmEventsGalleryViewCount} from 'features/services/googleTagManager/events';

import './style.scss';

import {P} from 'components/atoms/Text/P';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {Container} from 'components/atoms/Layout/Container';
import {UserAgent} from 'components/atoms/UserAgent';
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import {ModalGallery} from 'components/modals/General/ModalGallery';
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';

interface Props {
    galleryType: GalleryType;
    images: EpiImage[];
    digitalShowingText?: string;
    digitalShowingLinkUrl?: string;
    putVideoLast?: boolean;
}

export const ImageGallery: React.FunctionComponent<Props> = ({
    images, galleryType,
    putVideoLast,
    digitalShowingLinkUrl, digitalShowingText
}) => {
    const { open, isOpen: isModal } = useModal();
    const isMobile = useDeviceStore(selectIsMobile);

    const [showMore, setShowMore] = useState<boolean>(false);
    const [galleryImages, setGalleryImages] = useState<{ original: string; thumbnail: string; description: string; originalAlt?: string }[]>([]);
    let navigationCount: number = 1; // For gtm
    const maxImg: number = isMobile ? 3 : 5;
    const imgGalleryBtnRef = useRef<HTMLButtonElement>(null);

    const onSlide = (_event: unknown) => {
        navigationCount++;
        return;
    };

    const onImageClick = (index: number) => () => {
        if (images === null || typeof images === 'undefined') return;

        gtmEventsGalleryOpen(galleryType)();

        open(
            <ModalGallery
                images={images}
                startIndex={index}
                onSlide={onSlide}
                videoUrl={digitalShowingLinkUrl}
                videoDescription={digitalShowingText}
                putVideoLast={putVideoLast}
            />,
            {fullscreen: true, ariaLabel: getFormattedMessage('jm.components.modalGallery.ariaLabel'), closeCallback: () => {
                    const count = navigationCount;
                    navigationCount = 1;

                    let precentage = Math.round((count / galleryImages.length) * 10) * 10;
                    if(precentage < 1) {
                        precentage = 1;
                    }
                    if(precentage > 100) {
                        precentage = 100;
                    }

                    gtmEventsGalleryViewCount(galleryType, parseInt(precentage.toString(), 10).toString())();
            }}
        );
    };

    useEffect(() => {
        setupGallery();
    }, []);

    const setupGallery = () => {
        if(images === null || typeof images === 'undefined') return;

        const tempImages: {original: string, thumbnail: string, description: string, originalAlt?: string}[] = [];

        images.map((image) => {
            tempImages.push({
                original: `${image.url}?${ImageSizes.SIZE_1600}`,
                thumbnail: `${image.url}?${ImageSizes.SIZE_300}`,
                description: image.description ? image.description : '.',
                originalAlt: image.altText ? image.altText : '.',
            })
        });

        setGalleryImages(tempImages);
    };

    const _getImageClass = (image: EpiImage) => {
        return classnames('image-gallery__image', {
            'image-gallery__image--tall': image.tall,
            'image-gallery__image--wide': !image.tall,
        });
    };

    const checkRatio = (image: EpiImage) => {
        const imageCopy = {...image};

        if(imageCopy.orgHeight && imageCopy.orgWidth && imageCopy.orgHeight > imageCopy.orgWidth) {
            imageCopy.tall = true;
        }
        if(imageCopy.ratio && imageCopy.ratio < 1.7) {
            imageCopy.widthBased = true;
        }
        return imageCopy;
    };

    const _toggleShowAll = () => {
        setShowMore(!showMore);
    };

    const getStandingImagesClass = () => {
        const count = !images ? 0 : images.slice(0, maxImg).filter(image => ((image.orgHeight && image.orgWidth && image.orgHeight > image.orgWidth) || image.tall)).length;
        if(count === 0) return 'image-gallery--no-standing';
        switch(count) {
            case 1:
                return 'image-gallery--one-standing';
            case 2:
                return 'image-gallery--two-standing';
            case 3:
                return 'image-gallery--three-standing';
            case 4:
                return 'image-gallery--four-standing';
            case 5:
                return 'image-gallery--five-standing';
            default:
                return '';
        }
    };

    const classes = classnames('image-gallery', getStandingImagesClass(), {
        'image-gallery--one-image': images && images.length === 1,
    });

    if(!images) return null;
    if(images.length === 1) return (
        <div className={classes}>
            <div className="image-gallery--one-image__image" onClick={onImageClick(0)}>
                <AsyncImage
                    fullHeight={!images[0].tall}
                    fullWidth={images[0].tall}
                    key={`image-${images[0].url}`}
                    className=""
                    quality={ImageSizes.SIZE_2560}
                    image={images[0]}
                />
            </div>
            {images[0].description &&
                <Container narrow>
                    <P className="image-gallery__image__description">{images[0].description}</P>
                </Container>
            }
        </div>
    );
    return (
        <div className={classes}>
            <MainGrid className="image-gallery__tiles">
                {images.slice(0, maxImg).map(checkRatio).map((image, index) => (
                    <div
                        key={`image-${image.url}-${index}`}
                        className={
                            image.tall ?
                                'image-gallery__image image-gallery__image--tall' :
                                'image-gallery__image image-gallery__image--wide'
                        }
                    >
                        <div className="image-gallery__image__wrapper" onClick={onImageClick(index)}>
                            <AsyncImage
                                fullHeight={!image.tall}
                                fullWidth={image.tall}
                                key={`image-${image.url}`}
                                quality={ImageSizes.SIZE_900}
                                className=""
                                image={image}
                            />
                        </div>
                    </div>
                ))}
                {!isModal && images.length > maxImg &&
                    <div className="image-gallery__link" onClick={onImageClick(0)}>
                        <Button
                            ref={imgGalleryBtnRef}
                            buttonColor={ButtonColor.DEFAULT}
                            buttonSize={ButtonSize.DEFAULT}
                            buttonType={ButtonType.DEFAULT}
                            onClick={onImageClick(0)}
                        >
                            <UserAgent mobile tablet>
                                <FormattedMessage id="jm.components.ImageGallery.allImages" />
                            </UserAgent>
                            <UserAgent computer>
                                <FormattedMessage
                                    id="jm.components.ImageGallery.allImagesWithCount"
                                    values={{ imageCount: images.length }}
                                />
                            </UserAgent>
                        </Button>
                    </div>
                }
            </MainGrid>
        </div>
    )
};
